import { useEffect, useState } from 'react'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { CrmApiError } from '../../http'
import { toastMerged } from '../../merges/useUndoMerge'
import {
  getContactMergePreview,
  mergeContacts,
  type ContactBrief,
  type ContactMergeField,
  type ContactMergeMoves,
  type ContactMergePreview,
} from './api'

const FIELD_LABELS: Record<ContactMergeField, string> = {
  full_name: 'Naam',
  email: 'E-mail',
  phone: 'Telefoon',
  title: 'Functie',
  salutation: 'Aanhef',
  owner_user_id: 'Eigenaar',
  category_id: 'Categorie',
  status: 'Status',
  summary_html: 'Over het contact',
  linkedin_url: 'LinkedIn',
  relation_since: 'Relatie sinds',
  introduced_by_person_id: 'Geïntroduceerd door',
}

type Side = 'survivor' | 'other'

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === ''

function valueText(field: ContactMergeField, value: unknown, readable?: string | null): string {
  if (isEmpty(value)) return '—'
  if (readable) return readable
  const text = String(value)
  if (field !== 'summary_html') return text
  return new DOMParser().parseFromString(text, 'text/html').body.textContent?.trim() || '—'
}

function count(amount: number, one: string, many: string): string | null {
  if (amount <= 0) return null
  return amount === 1 ? `1 ${one}` : `${amount} ${many}`
}

/** "3 labels · 2 organisaties · 14 activiteiten · …": only what there is. */
function movesSummary(moves: ContactMergeMoves): string {
  return [
    count(moves.labels_moved, 'label', 'labels'),
    count(moves.organisations_moved + moves.organisations_folded, 'organisatie', 'organisaties'),
    count(moves.lists_moved + moves.lists_folded, 'lijst', 'lijsten'),
    count(moves.activities, 'activiteit', 'activiteiten'),
    count(moves.agreements, 'afspraak', 'afspraken'),
    count(moves.tasks, 'taak', 'taken'),
    count(moves.mails, 'mail', 'mails'),
    count(moves.consents, 'toestemming', 'toestemmingen'),
    count(moves.card_scans, 'visitekaartje', 'visitekaartjes'),
    count(moves.saved_views, 'opgeslagen weergave', 'opgeslagen weergaven'),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')
}

function describe(contact: ContactBrief): string {
  return [contact.full_name, contact.email, contact.organisation_name].filter(Boolean).join(' · ')
}

/**
 * Merge two contacts (spec-68): pick the one that stays, choose per differing field whose value
 * wins, see what moves, confirm. A founder can only stay. The preview's `updated_at` stamps go back
 * with the merge, so a change in between is refused (409) instead of overwritten.
 */
export function MergeContactsDialog({
  firstId,
  secondId,
  survivorId: preferredSurvivorId,
  onClose,
  onMerged,
  onUndone,
}: {
  firstId: string
  secondId: string
  /** The one that stays, when the caller already knows (the contact page). */
  survivorId?: string
  onClose: () => void
  onMerged: (survivor: { id: string; name: string }) => void
  /** After "Ongedaan maken" in the toast. */
  onUndone?: () => void
}) {
  const [survivorId, setSurvivorId] = useState(preferredSurvivorId ?? firstId)
  const otherId = survivorId === firstId ? secondId : firstId
  const [preview, setPreview] = useState<ContactMergePreview | null>(null)
  const [choices, setChoices] = useState<Partial<Record<ContactMergeField, Side>>>({})
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getContactMergePreview(survivorId, otherId)
      .then((answer) => {
        if (cancelled) return
        const initial: Partial<Record<ContactMergeField, Side>> = {}
        for (const difference of answer.fields) {
          // An empty survivor value is filled from the other contact anyway.
          initial[difference.field] = isEmpty(difference.survivor) ? 'other' : 'survivor'
        }
        setChoices(initial)
        setPreview(answer)
        setLoadError(null)
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught instanceof Error ? caught.message : 'Laden mislukt.')
      })
    return () => {
      cancelled = true
    }
  }, [survivorId, otherId, reloadKey])

  async function merge(current: ContactMergePreview) {
    setBusy(true)
    setError(null)
    try {
      const survivor = await mergeContacts({
        survivor_id: current.survivor.id,
        other_id: current.other.id,
        take_from_other: current.fields
          .filter((difference) => choices[difference.field] === 'other')
          .map((difference) => difference.field),
        expected: {
          survivor_updated_at: current.survivor_updated_at,
          other_updated_at: current.other_updated_at,
        },
      })
      setConfirming(false)
      toastMerged('contact', survivor.person_id, `Samengevoegd met ${current.survivor.full_name}`, onUndone)
      onMerged({ id: survivor.person_id, name: current.survivor.full_name })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Samenvoegen mislukt.')
      if (caught instanceof CrmApiError && caught.status === 409) {
        // Someone changed one of them: show the news and let the person decide again.
        setConfirming(false)
        setReloadKey((key) => key + 1)
      }
    } finally {
      setBusy(false)
    }
  }

  const founderBlocks = preview?.other.is_founder === true
  const summary = preview ? movesSummary(preview.moves) : ''
  return (
    <>
      <Dialog open onOpenChange={(next) => (!next && !busy ? onClose() : undefined)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Contacten samenvoegen</DialogTitle>
            <DialogDescription>
              Kies welk contact blijft bestaan. Het andere verdwijnt; alles wat eraan hangt gaat mee.
            </DialogDescription>
          </DialogHeader>
          {loadError ? (
            <p role="alert" className="text-sm text-destructive">
              {loadError}
            </p>
          ) : !preview ? (
            <div className="flex flex-col gap-2" aria-hidden>
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <fieldset className="flex flex-col gap-1">
                <legend className="text-sm font-semibold">Welk contact blijft over?</legend>
                {[preview.survivor, preview.other].map((contact) => (
                  <label key={contact.id} className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="survivor"
                      checked={contact.id === preview.survivor.id}
                      onChange={() => setSurvivorId(contact.id)}
                      className="mt-1"
                    />
                    <span>{describe(contact)}</span>
                  </label>
                ))}
              </fieldset>
              {founderBlocks ? (
                <div role="status" className="flex flex-wrap items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    {preview.other.full_name} is founder bij een startup en kan alleen overblijver zijn.
                  </span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSurvivorId(preview.other.id)}>
                    {preview.other.full_name} laten blijven
                  </Button>
                </div>
              ) : null}
              {preview.fields.length > 0 ? (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">Verschillende gegevens</h3>
                  {preview.fields.map((difference) => {
                    const name = `field-${difference.field}`
                    const label = FIELD_LABELS[difference.field]
                    const own = valueText(difference.field, difference.survivor, difference.survivor_text)
                    const theirs = valueText(difference.field, difference.other, difference.other_text)
                    return (
                      <fieldset key={difference.field} className="flex flex-col gap-1 text-sm">
                        <legend className="font-medium">{label}</legend>
                        <label className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={name}
                            aria-label={`${label}: ${own} (blijft)`}
                            checked={choices[difference.field] === 'survivor'}
                            onChange={() => setChoices((current) => ({ ...current, [difference.field]: 'survivor' }))}
                            className="mt-1"
                          />
                          <span>{own}</span>
                        </label>
                        <label className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={name}
                            aria-label={`${label}: ${theirs} (andere)`}
                            checked={choices[difference.field] === 'other'}
                            onChange={() => setChoices((current) => ({ ...current, [difference.field]: 'other' }))}
                            className="mt-1"
                          />
                          <span>{theirs}</span>
                        </label>
                        {isEmpty(difference.survivor) ? (
                          <p className="text-xs text-muted-foreground">
                            Leeg bij het contact dat blijft: de waarde van het andere wordt overgenomen.
                          </p>
                        ) : null}
                      </fieldset>
                    )
                  })}
                </section>
              ) : null}
              <section className="flex flex-col gap-1">
                <h3 className="text-sm font-semibold">Wat er verhuist</h3>
                <p className="text-sm">{summary || 'Niets: het andere contact heeft niets om mee te nemen.'}</p>
              </section>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Annuleren
            </Button>
            <Button type="button" disabled={!preview || busy || founderBlocks} onClick={() => setConfirming(true)}>
              Samenvoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {preview ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Samenvoegen?"
          description={`${preview.other.full_name} samenvoegen met ${preview.survivor.full_name}?${summary ? ` ${summary} gaan naar ${preview.survivor.full_name}.` : ''} ${preview.other.full_name} verdwijnt.`}
          confirmLabel="Samenvoegen"
          busy={busy}
          error={error}
          onConfirm={() => void merge(preview)}
        />
      ) : null}
    </>
  )
}
